import { Paper } from '@mui/material';
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { DEFAULT_SAS_COLORS } from './lineModel';
import type { SasColorRole, SasExchangeRange, SasModelLine } from './lineModel';

export type DecodeStreamProps = {
  /** The single line-model shared with the .txt exporter (`exportLineModelText`
   * in `lineModel.ts`) — this component never derives its own text. */
  lines: SasModelLine[];
  /** Per-role hex overrides (Task 3's per-user palette). Falls back to the
   * Option-C defaults for any role not supplied. */
  colors?: Partial<Record<SasColorRole, string>>;
  /** Overrides the container's `data-testid`. Defaults to
   * `sas-decode-stream`; the Colours dialog's live preview strip passes
   * `sas-colors-preview` so it doesn't collide with the page's own stream
   * in a test that renders both at once. */
  testId?: string;
  /** The command-search nav's currently-matched exchange, as a model-line
   * index range (`buildExchangeRanges` in `lineModel.ts`) — every row in
   * `[start, end]` gets a visual highlight. Purely a view concern: it never
   * touches `lines`' span text, so the .txt export (`exportLineModelText`)
   * is unaffected regardless of what's highlighted. `null`/omitted = no
   * highlight. */
  highlightRange?: SasExchangeRange | null;
  /** Bumped by the caller on every prev/next nav step (even a wrap-around
   * back to the same single match) so the highlighted exchange's first row
   * is scrolled into view (`block: 'nearest'`) each time — independent of
   * whether `highlightRange`'s value actually changed. */
  scrollToken?: number;
};

const roleColor = (role: SasColorRole): string => `var(--sas-${role})`;

/**
 * Renders the decode stream from a `SasModelLine[]` — the exact model the
 * .txt exporter serialises, so screen and export can never diverge. Colour is
 * applied ONLY via `--sas-*` CSS custom properties on the container (roles,
 * never a literal hex in a span); Task 3's per-user palette overrides the
 * variable *values* here without this component or the line-model builder
 * changing.
 *
 * Rows/spans are plain DOM nodes rather than MUI `Box` — a full decode can
 * carry thousands of lines (MaxLines), and per-span Emotion class generation
 * at that volume is measurably slower than native inline styles.
 */
export default function DecodeStream({
  lines, colors, testId = 'sas-decode-stream', highlightRange = null, scrollToken,
}: DecodeStreamProps) {
  const firstHighlightedRowRef = useRef<HTMLDivElement | null>(null);

  // Scroll the matched exchange into view on every nav step — keyed on
  // `scrollToken`, not on `highlightRange`'s value, so a wrap-around back to
  // the same single match still scrolls (an identical range object would
  // otherwise not re-trigger a value-keyed effect).
  useEffect(() => {
    if (highlightRange) firstHighlightedRowRef.current?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToken]);

  const containerVars = {
    '--sas-tx': colors?.tx ?? DEFAULT_SAS_COLORS.tx,
    '--sas-rx': colors?.rx ?? DEFAULT_SAS_COLORS.rx,
    '--sas-text': colors?.text ?? DEFAULT_SAS_COLORS.text,
    '--sas-frame': colors?.frame ?? DEFAULT_SAS_COLORS.frame,
    '--sas-dim': colors?.dim ?? DEFAULT_SAS_COLORS.dim,
    '--sas-ok': colors?.ok ?? DEFAULT_SAS_COLORS.ok,
    '--sas-err': colors?.err ?? DEFAULT_SAS_COLORS.err,
  } as CSSProperties;

  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      style={containerVars}
      sx={{
        p: 1.5,
        bgcolor: '#0e1116',
        overflowX: 'auto',
        fontFamily: 'monospace',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {lines.map((line, i) => {
        const isHighlighted = !!highlightRange && i >= highlightRange.start && i <= highlightRange.end;
        const isFirstHighlightedRow = isHighlighted && i === highlightRange!.start;
        return (
          <div
            key={i}
            ref={isFirstHighlightedRow ? firstHighlightedRowRef : undefined}
            data-testid="sas-decode-line"
            data-sas-highlighted={isHighlighted ? 'true' : undefined}
            style={{
              whiteSpace: 'pre',
              minHeight: '1.5em',
              // Highlight is view-only — a background tint + left-edge
              // stripe derived from the frame role (never err/ok; those are
              // reserved for error/good-value semantics). The transparent
              // left border on every row (not just highlighted ones)
              // reserves the 3px so highlighting never shifts layout.
              backgroundColor: isHighlighted ? 'color-mix(in srgb, var(--sas-frame) 18%, transparent)' : undefined,
              borderLeft: isHighlighted ? '3px solid var(--sas-frame)' : '3px solid transparent',
            }}
          >
            {line.map((span, j) => (
              <span key={j} data-sas-role={span.role} style={{ color: roleColor(span.role) }}>
                {span.text}
              </span>
            ))}
          </div>
        );
      })}
    </Paper>
  );
}
